import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import PaywallModal from "@/components/feature/PaywallModal";
import { stockScreener, type ScreenerItem } from "@/api/screener";

import { addToWatchlist, getQuote } from "@/api/market";
import { placeSimOrder, getSimAccount } from "@/api/sim";
const TEMPLATES = [
  { label: "低估蓝筹", icon: "ri-building-2-line", params: { markets: ["沪深主板"], peMax: 15, capMin: 500 } },
  { label: "放量突破", icon: "ri-rocket-line", params: { changeMin: 3, changeMax: 8, turnoverRateMin: 3, fundMin: 5, capMin: 50 } },
  { label: "主力建仓", icon: "ri-funds-line", params: { changeMin: 0, changeMax: 5, fundMin: 5, turnoverRateMin: 2, peMin: 0 } },
  { label: "超跌反弹", icon: "ri-arrow-down-circle-line", params: { changeMax: -5, capMin: 50, peMin: 0 } },
  { label: "小盘黑马", icon: "ri-flashlight-line", params: { capMax: 100, turnoverRateMin: 5, changeMin: 2, peMin: 0 } },
  { label: "成长先锋", icon: "ri-seedling-line", params: { peMin: 20, peMax: 60, capMax: 500, markets: ["创业板", "科创板"] } },
  { label: "高股息", icon: "ri-gift-line", params: { peMax: 15, capMin: 200, markets: ["沪深主板"] } },
  { label: "次新掘金", icon: "ri-shining-line", params: { capMax: 300, turnoverRateMin: 3, markets: ["创业板", "科创板"] } },
];

const CHIP_OPTIONS = {
  markets: ["沪深主板", "科创板", "创业板", "北交所"],
  industries: ["白酒", "电池", "半导体", "新能源", "光伏", "医药", "消费电子", "银行", "券商", "军工", "地产", "汽车", "电力", "化工", "机械", "软件"],
  changes: [
    { k: "急跌 <-5%", v: { changeMin: -10, changeMax: -5 } },
    { k: "下跌 -5~0", v: { changeMin: -5, changeMax: 0 } },
    { k: "小涨 0~3%", v: { changeMin: 0, changeMax: 3 } },
    { k: "大涨 3~5%", v: { changeMin: 3, changeMax: 5 } },
    { k: "涨停 >5%", v: { changeMin: 5, changeMax: 10 } },
  ],
  turnoverRates: [
    { k: "冷清 <1%", v: { turnoverRateMin: 0, turnoverRateMax: 1 } },
    { k: "正常 1~3%", v: { turnoverRateMin: 1, turnoverRateMax: 3 } },
    { k: "活跃 3~5%", v: { turnoverRateMin: 3, turnoverRateMax: 5 } },
    { k: "热门 5~10%", v: { turnoverRateMin: 5, turnoverRateMax: 10 } },
    { k: "爆量 >10%", v: { turnoverRateMin: 10, turnoverRateMax: 99 } },
  ],
  funds: [
    { k: "大幅流出", v: { fundMin: -100, fundMax: -5 } },
    { k: "流出", v: { fundMin: -5, fundMax: -1 } },
    { k: "平衡", v: { fundMin: -1, fundMax: 1 } },
    { k: "流入", v: { fundMin: 1, fundMax: 5 } },
    { k: "大幅流入", v: { fundMin: 5, fundMax: 100 } },
  ],
};

function toChip(s: string) { const m: Record<string,string> = { 主板: "沪", 创业板: "创", 科创板: "科", 北交所: "北" }; return m[s] || s; }

export default function StockScreener() {
  const nav = useNavigate();
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [results, setResults] = useState<ScreenerItem[]>([]);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("change_pct");
  const [sortDir, setSortDir] = useState("desc");
  const [toast, setToast] = useState("");
  const sub = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [savedFilters, setSavedFilters] = useState<{name:string,filters:any,activeTemplate:number|null}[]>(() => { try { return JSON.parse(localStorage.getItem('screener_saved')||'[]'); } catch { return []; }});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => { if (toast) { const tm = setTimeout(() => setToast(""), 2000); return () => clearTimeout(tm); } }, [toast]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initializedRef = useRef(false);

  const activeCount = Object.keys(filters).length;

  const doScreen = useCallback(async (extra?: Record<string, any>, append = false, currentPage = 1) => {
    const p = append ? currentPage + 1 : 1;
    const params: any = { ...filters, sortBy, sortDir, ...extra, page: p, pageSize: 20 };
  if (params.markets) { const m = params.markets.map((x: string) => x === "沪深主板" ? "主板" : x); params.markets = m; }
    setLoading(true);
    try {
      const d = await stockScreener(params);
      if (append) {
        setResults(prev => [...prev, ...(d.list || [])]);
        setPage(p);
      } else {
        setResults(d.list || []);
        setPage(1);
      }
      setTotal(d.total || 0);
      setHasMore((d.list?.length || 0) >= 20);
    } catch { if (!append) setResults([]); setTotal(0); setHasMore(false); }
    finally { setLoading(false); }
  }, [filters, sortBy, sortDir]);

  // Periodic price refresh without re-running the full screener query.
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    try {
      const saved = sessionStorage.getItem("screener_filters");
      if (saved) {
        const f = JSON.parse(saved);
        setFilters(f.filters || {});
        if (f.activeTemplate != null) setActiveTemplate(f.activeTemplate);
        setSortBy(f.sortBy || "change_pct");
        setSortDir(f.sortDir || "desc");
        if (f.filters && Object.keys(f.filters).length > 0) doScreen(f.filters);
        else doScreen();
        return;
      }
    } catch {
      // Ignore stale or malformed session filters and use defaults.
    }
    doScreen();
  }, [doScreen]);

  useEffect(() => {
    const tick = async () => {
      if (results.length === 0) return;
      const updates = await Promise.all(
        results.map(async (s) => {
          const code = s.code.replace(/^(sh|sz|bj)/, "");
          const pf = code.startsWith("6") ? "sh" : (code.startsWith("0") || code.startsWith("3")) ? "sz" : "bj";
          try {
            const q = await getQuote(pf + code);
            return { code: s.code, price: q.quote?.price ?? s.price, changePct: q.quote?.changePct ?? s.changePct };
          } catch { return null; }
        })
      );
      const map = new Map(updates.filter(Boolean).map(u => [u!.code, u!]));
      if (map.size > 0) {
        setResults(prev => prev.map(s => map.has(s.code) ? { ...s, price: map.get(s.code)!.price, changePct: map.get(s.code)!.changePct } : s));
      }
    };
    // 上游版本每秒逐个刷新筛选结果，20只股票会产生约20 QPS。
    // 预发布版本降低为15秒，避免多用户同时使用时冲击行情接口。
    timerRef.current = setInterval(tick, 15_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [results]);

  const applyTemplate = (idx: number) => {
    setActiveTemplate(idx);
    const p = TEMPLATES[idx].params;
    setFilters({ ...p });
  };

  const toggleChip = (key: string, val: any, key2?: string, val2?: any) => {
    if (key2 && val2 !== undefined) {
      setActiveTemplate(null);
      setFilters(prev => ({ ...prev, [key]: val, [key2]: val2 }));
      return;
    }
    setActiveTemplate(null);
    setFilters(prev => {
      const cur = prev[key];
      if (cur === undefined) return { ...prev, [key]: val };
      if (typeof cur === "object" && cur.min !== undefined) {
        return { ...prev, [key]: val };
      }
      return { ...prev, [key]: val };
    });
  };

  const setRange = (key: string, val: string) => {
    setActiveTemplate(null);
    const n = val === "" ? undefined : parseFloat(val);
    setFilters(prev => {
      if (n === undefined) { const { [key]: _, ...rest } = prev; return rest; }
      return { ...prev, [key]: n };
    });
  };

  const addPos = async (code: string, name: string) => {
    try { const acc = await getSimAccount(); if (!acc?.id) { setToast("请先开启模拟盘"); return; } const pf = code.startsWith("6") ? "sh" : (code.startsWith("0") || code.startsWith("3")) ? "sz" : "bj"; await placeSimOrder({ ticker: pf + code, side: "buy", qty: 100 }); setToast(name + " +持仓100股"); } catch (e) { setToast(e instanceof Error ? e.message : "+持仓失败"); }
  };

  const addWatch = async (code: string, name: string) => {
    try { const pf = code.startsWith("6") ? "sh" : (code.startsWith("0") || code.startsWith("3")) ? "sz" : "bj"; await addToWatchlist(pf + code); setToast(name + " +自选"); } catch { setToast("+自选失败"); }
  };

  const saveFilter = () => { const name = prompt("给这个筛选方案起个名字"); if (!name) return; const item = { name, filters, activeTemplate }; const updated = [...savedFilters, item]; setSavedFilters(updated); localStorage.setItem('screener_saved', JSON.stringify(updated)); setToast("已保存: " + name); };

  const loadSavedFilter = (idx: number) => { const item = savedFilters[idx]; setFilters(item.filters || {}); setActiveTemplate(item.activeTemplate); doScreen(); setToast("已加载: " + item.name); };

  const deleteSavedFilter = (idx: number) => { const updated = savedFilters.filter((_,i) => i !== idx); setSavedFilters(updated); localStorage.setItem('screener_saved', JSON.stringify(updated)); };

  const reset = () => { setFilters({}); setActiveTemplate(null); };
  useEffect(() => {
    sessionStorage.setItem("screener_filters", JSON.stringify({ filters, activeTemplate, sortBy, sortDir }));
  }, [filters, activeTemplate, sortBy, sortDir]);

  const selectedChips = (key: string) => {
    const v = filters[key];
    if (v === undefined) return new Set<string>();
    if (Array.isArray(v)) return new Set(v);
    return new Set<string>();
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-foreground-900 text-white text-xs font-semibold shadow-lg">
          {toast}
        </div>
      )}
      {/* Templates */}
      <div className="px-4 mb-3">
        <div className="text-[10px] text-foreground-400 mb-1.5">快速模板</div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {TEMPLATES.map((t, i) => (
            <button key={i} onClick={() => applyTemplate(i)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer border ${
                activeTemplate === i
                  ? "bg-primary-50 border-primary-500 text-primary-600"
                  : "bg-white border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-500"
              }`}>
              <i className={`${t.icon} mr-1 text-[11px]`} />
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {savedFilters.length > 0 && (
        <div className="px-4 mb-2">
          <div className="text-[10px] text-foreground-400 mb-1">我的方案</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {savedFilters.map((sf, i) => (
              <div key={i} className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border bg-white border-background-200">
                <button onClick={() => loadSavedFilter(i)} className="cursor-pointer text-foreground-600 hover:text-primary-500">{sf.name}</button>
                <button onClick={(e) => { e.stopPropagation(); deleteSavedFilter(i); }} className="text-foreground-400 hover:text-red-500 cursor-pointer text-[10px]">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Header */}
      <div className="px-4 mb-2">
        <button onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-1.5 text-xs text-foreground-400 cursor-pointer">
          <span>自定义筛选{activeCount > 0 ? ` · ${activeCount}项` : ""}</span>
          <i className={`ri-arrow-${showFilters ? "up" : "down"}-s-line text-[10px]`} />
        </button>
      </div>

      {/* Filter Body */}
      {showFilters && (
        <div className="mx-4 mb-3 bg-white rounded-xl p-3 border border-background-200/60 space-y-2.5">
          {/* 市场 */}
          <FilterRow label="市场">
            <ChipGroup options={CHIP_OPTIONS.markets} selected={selectedChips("markets")}
              display={(v) => v} onToggle={(v) => {
                setFilters(prev => {
                  const cur: string[] = prev.markets || [];
                  const next = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v];
                  return next.length ? { ...prev, markets: next } : { ...prev, markets: undefined };
                });
                setActiveTemplate(null);
              }} />
          </FilterRow>
          {/* 涨跌幅 */}
          <FilterRow label="涨跌幅">
            {CHIP_OPTIONS.changes.map((c, i) => (
              <Chip key={i} label={c.k} active={filters.changeMin === c.v.changeMin && filters.changeMax === c.v.changeMax}
                onToggle={() => toggleChip("changeMin", c.v.changeMin, "changeMax", c.v.changeMax)} />
            ))}
          </FilterRow>
          {/* 换手率 */}
          <FilterRow label="换手率">
            {CHIP_OPTIONS.turnoverRates.map((c, i) => (
              <Chip key={i} label={c.k} active={filters.turnoverRateMin === c.v.turnoverRateMin}
                onToggle={() => { toggleChip("turnoverRateMin", c.v.turnoverRateMin); toggleChip("turnoverRateMax", c.v.turnoverRateMax); }} />
            ))}
          </FilterRow>
          {/* 主力资金 */}
          <FilterRow label="主力资金">
            {CHIP_OPTIONS.funds.map((c, i) => (
              <Chip key={i} label={c.k} active={filters.fundMin === c.v.fundMin}
                onToggle={() => { toggleChip("fundMin", c.v.fundMin); toggleChip("fundMax", c.v.fundMax); }} />
            ))}
          </FilterRow>
          {/* 总市值 */}
          <RangeRow label="总市值" unit="亿" minKey="capMin" maxKey="capMax"
            minVal={filters.capMin} maxVal={filters.capMax} onChange={setRange} />
          {/* 市盈率 */}
          <RangeRow label="市盈率" unit="倍" minKey="peMin" maxKey="peMax"
            minVal={filters.peMin} maxVal={filters.peMax} onChange={setRange} />
          {/* 流通市值 */}
          <RangeRow label="流通市值" unit="亿" minKey="floatCapMin" maxKey="floatCapMax"
            minVal={filters.floatCapMin} maxVal={filters.floatCapMax} onChange={setRange} />
          {/* 最新价 */}
          <RangeRow label="最新价" unit="元" minKey="priceMin" maxKey="priceMax"
            minVal={filters.priceMin} maxVal={filters.priceMax} onChange={setRange} />
          {/* 市净率 */}
          <RangeRow label="市净率" unit="倍" minKey="pbMin" maxKey="pbMax"
            minVal={filters.pbMin} maxVal={filters.pbMax} onChange={setRange} />
          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button onClick={() => doScreen()}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-accent-500 cursor-pointer">筛选</button>
            <button onClick={reset}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-foreground-500 bg-background-100 cursor-pointer">重置</button>
            <button onClick={saveFilter}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-accent-500 to-primary-500 cursor-pointer">保存筛选</button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="px-4 pb-20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground-950">
            {total > 0 ? `找到 ${total} 只 · 限时免费` : loading ? "筛选中..." : "点击「筛选」查看结果"}
          </span>
          <button onClick={() => { const d = sortDir === "desc" ? "asc" : "desc"; setSortDir(d); setSortBy("change_pct"); doScreen({sortBy: "change_pct", sortDir: d}, false); }}
            className="text-xs text-primary-500 cursor-pointer">
            涨跌幅 {sortDir === "desc" ? "↓" : "↑"}
          </button>
        </div>
        {results.length === 0 && !loading ? (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-3xl text-foreground-300" />
            <p className="text-sm text-foreground-500 mt-2">没有符合条件的股票</p>
            <p className="text-xs text-foreground-400 mt-1">试试放宽筛选条件</p>
          </div>
        ) : (
          <div className="space-y-2">
            {results.map((s, i) => (
              <div key={s.code + i} onClick={() => nav(`/stock/${s.code.replace(/^(sh|sz|bj)/, "")}`)}
                className="bg-white rounded-xl p-3.5 border border-background-200/60 cursor-pointer hover:border-primary-200 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-sm font-bold text-foreground-950">{s.name}</span>
                    <span className="text-xs text-foreground-400 ml-2">{s.code.replace(/^(sh|sz|bj)/, "")}</span>
                    <div className="text-[10px] text-foreground-400 mt-0.5">{s.market} · {s.industry || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${s.changePct >= 0 ? "text-red-500" : "text-green-500"}`}>
                      {s.changePct >= 0 ? "+" : ""}{s.changePct.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-x-3 gap-y-1 mb-2">
                  <Meta label="价格" value={`¥${s.price.toFixed(2)}`} />
                  <Meta label="PE" value={s.pe > 0 ? s.pe.toFixed(0) : "亏损"} />
                  <Meta label="市值" value={`${(s.totalCap / 1e8).toFixed(0)}亿`} />
                  <Meta label="换手" value={`${s.turnoverRate.toFixed(1)}%`} />
                  <Meta label="主力" value={`${s.mainNet >= 0 ? "+" : ""}${s.mainNet.toFixed(1)}亿`}
                    valueClass={s.mainNet >= 0 ? "text-red-500" : "text-green-500"} />
                  <Meta label="成交额" value={`${(s.turnover / 1e8).toFixed(1)}亿`} />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={(e) => { e.stopPropagation(); if (!sub.subscribed) { setShowPaywall(true); return; } addWatch(s.code.replace(/^(sh|sz|bj)/, ""), s.name); }}
                    className="flex-1 py-1.5 rounded-lg text-[10px] font-semibold border border-background-200 text-foreground-500 bg-white cursor-pointer hover:bg-background-50">+自选</button>
                  <button onClick={(e) => { e.stopPropagation(); if (!sub.subscribed) { setShowPaywall(true); return; } addPos(s.code.replace(/^(sh|sz|bj)/, ""), s.name); }}
                    className="flex-1 py-1.5 rounded-lg text-[10px] font-semibold border border-background-200 text-foreground-500 bg-white cursor-pointer hover:bg-background-50">+持仓100股</button>
                  <button onClick={(e) => { e.stopPropagation(); if (!sub.subscribed) { setShowPaywall(true); return; } nav(`/research/generate/${s.code.replace(/^(sh|sz|bj)/, "")}?step=quiz&name=${encodeURIComponent(s.name)}`); }}
                    className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold border border-primary-200 text-primary-600 bg-primary-50 cursor-pointer hover:bg-primary-100 transition-colors">生成研报</button>
                </div>
              </div>
            ))}
          </div>
        )}
      {hasMore && (
        <div className="text-center mt-3">
          <button onClick={() => doScreen(undefined, true, page)}
            disabled={loading}
            className="px-6 py-2 rounded-full text-xs font-semibold border border-primary-200 text-primary-600 bg-primary-50 cursor-pointer hover:bg-primary-100 disabled:opacity-50">
            {loading ? "加载中..." : `加载更多 (${total - results.length} 只剩余)`}
          </button>
        </div>
      )}
      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        description="该操作需要先开通权限，请联系在线客服"
      />
      </div>
    </div>
  );
}

// ─── Sub-components ───

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-foreground-500 w-12 shrink-0">{label}</span>
      <div className="flex gap-1.5 flex-wrap flex-1">{children}</div>
    </div>
  );
}

function RangeRow({ label, unit, minKey, maxKey, minVal, maxVal, onChange }: {
  label: string; unit: string; minKey: string; maxKey: string;
  minVal?: number; maxVal?: number;
  onChange: (key: string, val: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-foreground-500 w-12 shrink-0">{label}</span>
      <div className="flex gap-1.5 items-center flex-1">
        <input type="number" placeholder="最小" value={minVal ?? ""}
          onChange={e => onChange(minKey, e.target.value)}
          className="w-[68px] px-2 py-1.5 text-xs border border-background-200 rounded-lg outline-none focus:border-primary-400 text-center" />
        <span className="text-foreground-300 text-xs">—</span>
        <input type="number" placeholder="最大" value={maxVal ?? ""}
          onChange={e => onChange(maxKey, e.target.value)}
          className="w-[68px] px-2 py-1.5 text-xs border border-background-200 rounded-lg outline-none focus:border-primary-400 text-center" />
        <span className="text-[10px] text-foreground-400">{unit}</span>
      </div>
    </div>
  );
}

function Chip({ label, active, onToggle }: { label: string; active: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle}
      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors cursor-pointer border ${
        active ? "bg-primary-50 border-primary-500 text-primary-600" : "bg-white border-background-200 text-foreground-500 hover:border-primary-300"
      }`}>
      {label}
    </button>
  );
}

function ChipGroup({ options, selected, display, onToggle }: {
  options: string[]; selected: Set<string>; display: (v: string) => string; onToggle: (v: string) => void;
}) {
  return options.map((opt, i) => (
    <Chip key={i} label={display(opt)} active={selected.has(opt)} onToggle={() => onToggle(opt)} />
  ));
}

function Meta({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="text-[10px]">
      <span className="text-foreground-400 mr-1">{label}</span>
      <span className={`font-semibold ${valueClass || "text-foreground-700"}`}>{value}</span>
    </div>
  );
}
